import { useState } from 'react';
import { Plus, Layers, Package, BarChart3 } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Tabs } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { ModuleTogglePanel } from '@/components/dashboard/ModuleTogglePanel';

/**
 * docs/IMPLEMENTATION_PLAN.md Feature 8 — the largest single feature in the
 * whole build (39 invocable surfaces, 9 tables). Own internal tabs match
 * the plan's explicit recommendation to give this its own sub-navigation
 * rather than flattening it into Community's outer tabs.
 */
export function ReactionRolesPage() {
  const [tab, setTab] = useState('panels');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge tone="community">Largest feature in the build</Badge>
        <span className="text-small text-text-tertiary">39 invocable surfaces across 9 database tables</span>
      </div>
      <ModuleTogglePanel module="reaction-roles" title="Reaction Roles" permissionNote="Manage Roles permission required." />

      <Tabs
        tabs={[
          { id: 'panels', label: 'Panels' },
          { id: 'bundles', label: 'Bundles' },
          { id: 'analytics', label: 'Analytics' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'panels' ? (
        <Card>
          <CardHeader
            title="Panels"
            description="Button or select-dropdown panels with prerequisite roles, conflicts, verification-gating, temporary roles, pricing, and minimum-level conditions."
            action={
              <Button variant="primary" size="sm" disabled>
                <Plus size={14} aria-hidden="true" />
                New panel
              </Button>
            }
          />
          <EmptyState
            icon={Layers}
            title="No panels yet"
            description="33 of this feature's subcommands are slash-only in v18 (built on Discord's raw app_commands.Group API, no prefix equivalent) — that distinction has no bearing on this UI, since it talks to the Bot Bridge either way."
          />
        </Card>
      ) : null}

      {tab === 'bundles' ? (
        <Card>
          <CardHeader
            title="Bundles"
            description="Grant multiple roles as one selectable unit."
            action={
              <Button variant="primary" size="sm" disabled>
                <Plus size={14} aria-hidden="true" />
                New bundle
              </Button>
            }
          />
          <EmptyState icon={Package} title="No bundles yet" />
        </Card>
      ) : null}

      {tab === 'analytics' ? (
        <Card>
          <CardHeader title="Historical activity" description="Role add/remove activity over time, per panel." />
          <EmptyState
            icon={BarChart3}
            title="No activity to show yet"
            description="The bot already tracks this in a dedicated table — it will appear here once connected, no new bot-side tracking needed."
          />
        </Card>
      ) : null}
    </div>
  );
}
